import {
  AI_SPREADSHEET_CELL_KIND,
  AI_SPREADSHEET_VALUE_TYPE,
  type IAiSpreadsheetCell,
  type CachedSpreadsheetCell,
  type IAiSpreadsheetManualRow,
  type IAiSpreadsheetSheetResponse,
  type IManualRowInput,
  type IManualRowsResponse,
} from "@/payroll/ai-script/types";
import { addSpreadsheetSheetCells } from "./sheet-cells";

export const OPTIMISTIC_MANUAL_ROW_ID_PREFIX = "optimistic-mr-";

const isGeneratedTextCellWithSuggestions = (
  cell: IAiSpreadsheetCell,
  col: number,
): cell is IAiSpreadsheetCell & { suggested_values: string[] } =>
  cell.col === col &&
  cell.kind === AI_SPREADSHEET_CELL_KIND.value &&
  cell.value_type === AI_SPREADSHEET_VALUE_TYPE.text &&
  cell.suggested_values != null &&
  cell.suggested_values.length > 0 &&
  cell.manual_row_id == null &&
  cell.source_ref?.manual_row_id == null;

export const inferColumnSuggestedValues = (
  cells: IAiSpreadsheetCell[],
  col: number,
): string[] | null => {
  const candidates = cells
    .filter((cell) => isGeneratedTextCellWithSuggestions(cell, col))
    .map((cell) => cell.suggested_values);
  const firstCandidate = candidates[0];
  if (!firstCandidate) return null;

  const isConsistent = candidates.every(
    (suggestions) =>
      suggestions.length === firstCandidate.length &&
      suggestions.every(
        (suggestion, index) => suggestion === firstCandidate[index],
      ),
  );
  return isConsistent ? [...firstCandidate] : null;
};

export const buildOptimisticManualCells = (
  sheetId: string,
  startRow: number,
  rowInputs: IManualRowInput[],
  token: string,
  sourceCells: IAiSpreadsheetCell[] = [],
  editableWhilePending = false,
  isInsert = false,
): CachedSpreadsheetCell[] => {
  const cells: CachedSpreadsheetCell[] = [];
  const columnSuggestions = new Map<number, string[] | null>();
  const suggestionsForColumn = (col: number) => {
    if (!columnSuggestions.has(col)) {
      columnSuggestions.set(col, inferColumnSuggestedValues(sourceCells, col));
    }
    return columnSuggestions.get(col) ?? null;
  };

  rowInputs.forEach((rowInput, index) => {
    const row = startRow + index;
    const manualRowId = `${OPTIMISTIC_MANUAL_ROW_ID_PREFIX}${token}-${row}`;
    Object.entries(rowInput.cells ?? {}).forEach(([col, spec]) => {
      const isFormula = spec.kind === AI_SPREADSHEET_CELL_KIND.formula;
      const kind = isFormula
        ? AI_SPREADSHEET_CELL_KIND.formula
        : AI_SPREADSHEET_CELL_KIND.value;
      const valueType = spec.value_type ?? AI_SPREADSHEET_VALUE_TYPE.text;
      const rawInput = isFormula ? spec.raw_input : String(spec.value ?? "");
      const computedValue = isFormula ? null : (spec.value ?? "");
      const columnNumber = Number(col);
      cells.push({
        id: `${manualRowId}-${col}`,
        ai_spreadsheet_sheet_id: sheetId,
        row,
        col: columnNumber,
        kind,
        raw_input: rawInput,
        computed_value: computedValue,
        value_type: valueType,
        format: null,
        error: null,
        accessor_key: null,
        source_ref: {
          manual_row_id: manualRowId,
          ...(editableWhilePending ? { editable_while_pending: true } : {}),
        },
        suggested_values:
          !isFormula && valueType === AI_SPREADSHEET_VALUE_TYPE.text
            ? suggestionsForColumn(columnNumber)
            : null,
        manual_row_id: manualRowId,
        ...(editableWhilePending && isInsert
          ? { pending_manual_row_insert: true }
          : {}),
        edited_by_admin_user_id: null,
        edited_at: null,
        created_at: "",
        updated_at: "",
      });
    });
  });
  return cells;
};

export const addOptimisticManualRows = (
  data: IAiSpreadsheetSheetResponse | undefined,
  {
    sheetId,
    rowInputs,
    token,
    afterRow,
    editableWhilePending = false,
  }: {
    sheetId: string;
    rowInputs: IManualRowInput[];
    token: string;
    afterRow: number | null;
    editableWhilePending?: boolean;
  },
) => {
  if (!data) return data;

  const startRow =
    afterRow != null
      ? afterRow + 1
      : data.sheet.cells.reduce((max, cell) => Math.max(max, cell.row), 0) + 1;
  const shiftedData =
    afterRow == null
      ? data
      : {
          ...data,
          sheet: {
            ...data.sheet,
            cells: data.sheet.cells.map((cell) =>
              cell.row > afterRow ? { ...cell, row: cell.row + 1 } : cell,
            ),
          },
        };

  return addSpreadsheetSheetCells(
    shiftedData,
    buildOptimisticManualCells(
      sheetId,
      startRow,
      rowInputs,
      token,
      data.sheet.cells,
      editableWhilePending,
      afterRow != null,
    ),
  );
};

export const reconcileCreatedManualRowCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  cells: IAiSpreadsheetCell[],
  optimisticToken?: string,
) => {
  if (!data) return data;

  const optimisticPrefix = optimisticToken
    ? `${OPTIMISTIC_MANUAL_ROW_ID_PREFIX}${optimisticToken}-`
    : null;
  const reconciledData = optimisticPrefix
    ? {
        ...data,
        sheet: {
          ...data.sheet,
          cells: data.sheet.cells.filter(
            (cell) => !cell.id.startsWith(optimisticPrefix),
          ),
        },
      }
    : data;

  return addSpreadsheetSheetCells(reconciledData, cells);
};

export const reconcileBulkPasteSheetCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  cells: IAiSpreadsheetCell[],
  optimisticToken?: string,
) =>
  reconcileCreatedManualRowCells(
    data,
    cells.filter((cell) => cell.ai_spreadsheet_sheet_id === data?.sheet.id),
    optimisticToken,
  );

export const mergeManualRows = (
  data: IManualRowsResponse | undefined,
  incomingRows: IAiSpreadsheetManualRow[],
): IManualRowsResponse | undefined => {
  if (!data) return data;

  const incomingById = new Map(incomingRows.map((row) => [row.id, row]));
  const existingIds = new Set(data.manual_rows.map((row) => row.id));

  return {
    manual_rows: [
      ...data.manual_rows.map((row) => incomingById.get(row.id) ?? row),
      ...incomingRows.filter((row) => !existingIds.has(row.id)),
    ],
  };
};
